import { Link } from "react-router-dom";
import {
  ArrowRight, LayoutGrid, Stethoscope, Building2, Banknote, ShieldCheck, Wrench,
  Handshake, Compass, Layers, Route, SlidersHorizontal, HeartHandshake,
  Quote, Home as HomeIcon, Droplets, ScanLine, Microscope, GraduationCap, Network, Briefcase,
} from "lucide-react";
import { MaskLines, Reveal, Stagger, StaggerItem } from "../components/Reveal";
import Marquee from "../components/Marquee";
import SectionHead from "../components/SectionHead";
import CTABand from "../components/CTABand";
import { trackEvent } from "../lib/api";
import { PRODUCTS, PROJECTS, POSTS, TESTIMONIALS } from "../data/content";

const HERO_IMG = "https://images.unsplash.com/photo-1764885517847-79d62138cc58?q=80&w=2400&auto=format&fit=crop";

const CAPABILITIES = [
  { icon: LayoutGrid, title: "Complete Healthcare Solutions", line: "From planning to operational support." },
  { icon: Stethoscope, title: "Medical Equipment", line: "Reliable equipment and healthcare products." },
  { icon: Building2, title: "Hospital Projects", line: "End-to-end support for healthcare facilities." },
  { icon: Banknote, title: "Financing Support", line: "Assistance in exploring suitable financing options." },
  { icon: ShieldCheck, title: "Licensing & Accreditation", line: "Support through regulatory and accreditation requirements." },
  { icon: Wrench, title: "After-Sales Support", line: "Maintenance, repair and technical assistance." },
];

const SOLUTIONS = [
  { id: "hospital-setup", span: "md:col-span-7", title: "Hospital Setup & Turnkey Solutions", img: HERO_IMG,
    blurb: "Comprehensive support for establishing and upgrading healthcare facilities — equipment, furniture, infrastructure requirements and project coordination.",
    chips: ["Turnkey coordination", "Infrastructure requirements", "Project execution"] },
  { id: "equipment", span: "md:col-span-5", title: "Medical Equipment & Devices", img: "https://images.unsplash.com/photo-1655635643486-a17bc48771ff?q=80&w=1600&auto=format&fit=crop",
    blurb: "A comprehensive portfolio of medical equipment, surgical products, diagnostic equipment, hospital furniture and healthcare consumables.",
    chips: ["Equipment", "Surgical products", "Consumables"] },
  { id: "dialysis", span: "md:col-span-5", title: "Dialysis Centre Solutions", img: "https://images.unsplash.com/photo-1778151270757-f3793f65e2de?q=80&w=1600&auto=format&fit=crop",
    blurb: "Support for dialysis centre planning, equipment, furniture and operational requirements.",
    chips: ["Dialysis chairs", "RO / water treatment", "Installation & service"] },
  { id: "ot-critical-care", span: "md:col-span-7", title: "OT & Critical Care", img: "https://images.unsplash.com/photo-1775947933085-30050ddad6b3?q=80&w=1600&auto=format&fit=crop",
    blurb: "Operating theatre and intensive care setups — from tables and lights to monitors and critical-care furniture.",
    chips: ["OT tables & lights", "Patient monitors", "ICU equipment"] },
  { id: "diagnostic", span: "md:col-span-6", title: "Diagnostic & Laboratory Solutions", img: "https://images.unsplash.com/photo-1602052577122-f73b9710adba?q=80&w=1600&auto=format&fit=crop",
    blurb: "Laboratory and diagnostic equipment, patient monitoring, supporting consumables and facility requirements.",
    chips: ["Lab equipment", "Diagnostic equipment", "Consumables"] },
  { id: "furniture", span: "md:col-span-6", title: "Hospital Furniture", img: "https://images.unsplash.com/photo-1778151270902-cb0ca572f2ee?q=80&w=1600&auto=format&fit=crop",
    blurb: "Beds, fowler and ICU beds, examination couches, OT tables, dialysis chairs, patient furniture and hospital storage.",
    chips: ["Hospital beds", "Patient furniture", "Storage"] },
];

const SETUP_STEPS = [
  { n: "01", title: "Requirement Assessment", line: "Bed capacity, departments, clinical requirements, budget, location and project timeline." },
  { n: "02", title: "Planning", line: "Equipment planning, department-wise requirements, procurement planning and infrastructure coordination." },
  { n: "03", title: "Procurement", line: "Medical equipment, furniture, surgical equipment, consumables and supporting products." },
  { n: "04", title: "Installation", line: "Delivery, installation, commissioning and user orientation for your teams." },
  { n: "05", title: "Compliance Support", line: "Assistance related to licensing, regulatory requirements and accreditation preparation." },
  { n: "06", title: "After-Sales", line: "Maintenance, repair, technical support, AMC support and replacement requirements." },
];

const PROCESS = [
  { title: "Understand", line: "We understand your clinical and operational requirements." },
  { title: "Plan", line: "We help map the appropriate products, infrastructure and project requirements." },
  { title: "Source", line: "We coordinate procurement from suitable manufacturers and suppliers." },
  { title: "Execute", line: "We support delivery, installation and commissioning." },
  { title: "Support", line: "We continue supporting the facility after implementation." },
];

const INDUSTRIES = [
  { icon: Building2, label: "Hospitals" }, { icon: "Home", label: "Nursing Homes" }, { icon: Stethoscope, label: "Clinics" },
  { icon: "Droplets", label: "Dialysis Centres" }, { icon: "ScanLine", label: "Diagnostic Centres" }, { icon: "Microscope", label: "Laboratories" },
  { icon: "GraduationCap", label: "Medical Institutions" }, { icon: "Network", label: "Healthcare Chains" }, { icon: "Briefcase", label: "Corporate Healthcare Facilities" },
];

const WHY = [
  { icon: Handshake, title: "One Healthcare Partner", line: "Multiple requirements coordinated through one partner." },
  { icon: Compass, title: "Project Understanding", line: "Experience supporting healthcare facility requirements." },
  { icon: Layers, title: "Product Range", line: "Medical equipment, furniture, consumables and healthcare products." },
  { icon: Route, title: "End-to-End Support", line: "From procurement through after-sales support." },
  { icon: SlidersHorizontal, title: "Flexible Solutions", line: "Solutions tailored to facility size and requirements." },
  { icon: HeartHandshake, title: "Long-Term Relationship", line: "Focus on continued support rather than one-time transactions." },
];

const Icon = ({ name, className }) => {
  const Lucide = { Home: HomeIcon, Droplets, ScanLine, Microscope, GraduationCap, Network, Briefcase }[name];
  return <Lucide className={className} />;
};

const products = PRODUCTS.filter((p) => p.featured).slice(0, 4);
const featuredProjects = PROJECTS.filter((p) => p.featured);
const posts = POSTS.slice(0, 3);

const Home = () => (
  <main data-testid="home-page">
    {/* HERO */}
    <section className="relative flex min-h-[100svh] items-end overflow-hidden bg-brand-950 grain" data-testid="hero">
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt="Modern hospital building — AccuSurge healthcare infrastructure solutions"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-brand-950/95 via-brand-950/75 to-brand-950/35" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-950 via-transparent to-brand-950/40" aria-hidden="true" />
      <div className="container-x relative pb-20 pt-40 md:pb-28">
        <span className="eyebrow text-accent hero-fade" style={{ animationDelay: "0.15s" }}>
          AccuSurge Medical Solutions Pvt. Ltd.
        </span>
        <h1 className="mt-7 max-w-5xl font-heading text-[2.6rem] leading-[1.04] sm:text-6xl md:text-7xl font-semibold tracking-tighter text-white" data-testid="hero-headline">
          <MaskLines
            lines={[
              <>Building Better Healthcare,</>,
              <>From <span className="text-accent">Concept to Care</span>.</>,
            ]}
            delay={0.35}
          />
        </h1>
        <p className="hero-fade mt-7 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg" style={{ animationDelay: "0.95s" }} data-testid="hero-subheading">
          AccuSurge Medical Solutions provides integrated healthcare solutions — from medical equipment and hospital
          infrastructure to project support, financing assistance, licensing, accreditation and after-sales service.
        </p>
        <div className="hero-fade mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: "1.1s" }} data-testid="hero-ctas">
          <Link to="/solutions" className="btn-accent" data-testid="hero-cta-solutions">
            Explore Our Solutions <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/contact"
            className="btn-ghost-light"
            data-testid="hero-cta-expert"
            onClick={() => trackEvent("consultation_request", { source: "hero" })}
          >
            Talk to Our Healthcare Expert
          </Link>
          <Link to="/contact?intent=quote" className="group inline-flex items-center gap-2 px-2 text-sm font-semibold text-white/80 transition-colors hover:text-accent" data-testid="hero-cta-quote">
            Request a Quote
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="pointer-events-none mt-14 max-w-xl" aria-hidden="true">
          <svg viewBox="0 0 720 120" className="h-14 w-full" fill="none">
            <path
              className="ecg-line"
              d="M0 78 H300 L336 78 362 18 396 118 424 78 470 78 H520 L540 54 560 96 576 78 H720"
              stroke="#F7941D" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </section>

    {/* CAPABILITY STRIP */}
    <section className="relative z-10 bg-white" data-testid="capability-strip">
      <div className="container-x">
        <Reveal className="-mt-14 relative">
          <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_70px_rgba(11,46,56,0.10)] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {CAPABILITIES.map((c, i) => (
              <div
                key={c.title}
                className={`group p-6 transition-colors hover:bg-brand-50 ${i > 0 ? "border-t sm:border-t-0 border-slate-100" : ""} ${i % 2 === 1 ? "sm:border-l" : ""} ${i >= 2 ? "lg:border-l" : ""} ${i >= 3 ? "lg:border-t xl:border-t-0" : ""} xl:border-l ${i === 0 ? "xl:border-l-0" : ""}`}
                data-testid={`capability-block-${i + 1}`}
              >
                <c.icon className="h-6 w-6 text-accent transition-transform duration-300 group-hover:-translate-y-0.5" />
                <h3 className="mt-4 font-heading text-[15px] font-semibold leading-snug text-ink">{c.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-500">{c.line}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <div className="mt-20" />

    {/* WHY ACCUSURGE */}
    <section className="py-24 md:py-32" data-testid="why-accusurge">
      <div className="container-x grid gap-16 lg:grid-cols-2">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <Reveal>
            <span className="eyebrow">Why AccuSurge?</span>
            <h2 className="mt-5 font-heading text-3xl md:text-5xl font-semibold tracking-tight text-ink text-balance">
              More Than a Supplier. Your Healthcare Solutions Partner.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-slate-600">
              Setting up or upgrading a healthcare facility involves much more than purchasing equipment. From planning
              and procurement to installation, compliance and ongoing maintenance, every component needs to work together.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-slate-600">
              AccuSurge brings multiple healthcare requirements under one roof, helping healthcare institutions simplify
              procurement, project execution and ongoing support.
            </p>
            <div className="mt-8 border-l-2 border-accent bg-brand-50 p-6">
              <p className="font-heading text-lg font-medium text-brand-800">
                From Medical Equipment to Complete Healthcare Solutions.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                AccuSurge Medical Solutions partners with healthcare providers to simplify procurement, project
                execution and ongoing support — helping turn healthcare plans into operational facilities.
              </p>
            </div>
          </Reveal>
        </div>
        <div>
          {["Plan", "Design", "Procure", "Install", "Commission", "Comply", "Maintain"].map((step, i) => (
            <Reveal key={step}>
              <div className="relative flex items-center gap-6 pb-10 last:pb-0" data-testid={`lifecycle-step-${i + 1}`}>
                {i < 6 && <span className="absolute left-[21px] top-12 h-[calc(100%-3rem)] w-px bg-slate-200" aria-hidden="true" />}
                <span className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brand-200 bg-white font-mono text-xs font-medium text-brand-700">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-ink">{step}</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {[
                      "Clinical and operational requirements mapped with your team.",
                      "Layouts, department plans and equipment mapping.",
                      "Coordinated sourcing from suitable manufacturers.",
                      "Delivery, installation and commissioning support.",
                      "Licensing and accreditation requirements coordinated.",
                      "Preventive maintenance, repair and AMC support.",
                    ][i]}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <Marquee />

    {/* HEALTHCARE SOLUTIONS */}
    <section className="py-24 md:py-32" data-testid="solutions-section">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead
            eyebrow="Our Healthcare Solutions"
            title="Six capabilities. One accountable partner."
            lede="Whether you are building a new facility or upgrading one, AccuSurge coordinates the equipment, infrastructure and support around your clinical goals."
          />
          <Reveal>
            <Link to="/solutions" className="btn-outline" data-testid="solutions-view-all">All Solutions <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
        <Stagger className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-6">
          {SOLUTIONS.map((s) => (
            <StaggerItem key={s.id} className={s.span}>
              <Link
                to={`/solutions#${s.id}`}
                className={`group relative block h-[300px] overflow-hidden rounded-xl md:h-[380px] ${s.id === "hospital-setup" ? "md:h-[420px]" : ""}`}
                data-testid={`solution-card-${s.id}`}
              >
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-950/95 via-brand-950/45 to-brand-950/10 transition-colors duration-500 group-hover:via-brand-950/60" aria-hidden="true" />
                <div className="absolute inset-x-0 bottom-0 p-7 md:p-9">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-white/70 opacity-0 transition-all duration-500 group-hover:opacity-100 md:translate-y-2 md:group-hover:translate-y-0 max-lg:opacity-100 max-lg:translate-y-0">
                    {s.blurb}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 max-lg:hidden">
                    {s.chips.map((c) => (
                      <span key={c} className="rounded-full border border-white/25 bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-white/85 backdrop-blur-sm">{c}</span>
                    ))}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-accent">
                    Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* PRODUCTS TEASER */}
    <section className="bg-brand-950 py-24 md:py-32 grain relative" data-testid="products-teaser">
      <div className="container-x relative">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead
            dark
            eyebrow="Products"
            title="Evidence behind the capability."
            lede="A searchable catalogue of medical equipment, furniture, consumables and devices — supporting every project we coordinate."
          />
          <Reveal>
            <Link to="/products" className="btn-ghost-light" data-testid="products-explore">Explore Products <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
        <Stagger className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p) => (
            <StaggerItem key={p.id}>
              <Link
                to={`/products/${p.slug}`}
                className="group block overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-colors hover:border-accent/60"
                data-testid={`teaser-product-${p.slug}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                </div>
                <div className="p-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent">{p.category.replace(/-/g, " ")}</span>
                  <h3 className="mt-2 font-heading text-lg font-semibold leading-snug text-white">{p.name}</h3>
                  <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-white/60">{p.short_description}</p>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* END-TO-END SETUP */}
    <section className="py-24 md:py-32" data-testid="setup-section">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead
            eyebrow="End-to-End Hospital Setup"
            title="From Healthcare Vision to Operational Facility."
            lede="AccuSurge supports healthcare institutions in setting up and upgrading facilities — coordinating equipment, furniture, infrastructure requirements and project execution."
          />
          <Reveal>
            <Link to="/hospital-setup" className="btn-accent" data-testid="setup-plan-cta">Plan Your Hospital <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
        <Stagger className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-6">
          {SETUP_STEPS.map((s) => (
            <StaggerItem key={s.n}>
              <div className="group h-full rounded-xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50 hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)]" data-testid={`setup-step-${s.n}`}>
                <span className="font-mono text-4xl font-medium text-brand-200 transition-colors group-hover:text-accent">{s.n}</span>
                <h3 className="mt-5 font-heading text-xl font-semibold text-ink">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.line}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* PROCESS */}
    <section className="bg-brand-900 py-24 md:py-28" data-testid="process-section">
      <div className="container-x">
        <SectionHead dark eyebrow="Our Process" title="How We Work" />
        <Stagger className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3 lg:grid-cols-5 lg:gap-6">
          {PROCESS.map((p, i) => (
            <StaggerItem key={p.title} className="relative">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-accent">{String(i + 1).padStart(2, "0")}</span>
                {i < 4 && <span className="hidden h-px flex-1 bg-white/15 lg:block" aria-hidden="true" />}
              </div>
              <h3 className="mt-4 font-heading text-xl font-semibold text-white">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{p.line}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* PROJECTS */}
    <section className="py-24 md:py-32" data-testid="projects-section">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead
            eyebrow="Projects"
            title="Facilities we have helped bring to life."
            lede="Healthcare facility projects coordinated end-to-end by AccuSurge."
          />
          <Reveal>
            <Link to="/projects" className="btn-outline" data-testid="projects-view-all">View Projects <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
        <Stagger className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {featuredProjects.map((p) => (
            <StaggerItem key={p.id}>
              <Link
                to={`/projects/${p.slug}`}
                className="group grid overflow-hidden rounded-xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] sm:grid-cols-2"
                data-testid={`project-card-${p.slug}`}
              >
                <div className="h-56 overflow-hidden sm:h-full">
                  <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                </div>
                <div className="p-7">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent-600">{p.facility_type}</span>
                  <h3 className="mt-2 font-heading text-xl font-semibold text-ink">{p.name}</h3>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600">{p.description}</p>
                  <span className="mt-4 inline-block rounded-full bg-brand-50 px-3 py-1 text-[11px] font-semibold text-brand-700">{p.status}</span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* FINANCING + LICENSING + AFTER-SALES */}
    <section className="bg-slate-100 py-24 md:py-32" data-testid="support-pillars">
      <div className="container-x">
        <SectionHead
          eyebrow="Beyond Supply"
          title="Support that reaches the operating certificate."
          lede="Healthcare projects need more than equipment. AccuSurge supports financing exploration, regulatory coordination and long-term service continuity."
        />
        <Stagger className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {[
            { icon: Banknote, title: "Healthcare Financing Support", line: "Assistance in exploring financing options through appropriate banking and financial channels for eligible healthcare projects.", cta: "Discuss Your Project", to: "/services#financing" },
            { icon: ShieldCheck, title: "Licensing & Accreditation Support", line: "Support navigating regulatory, licensing and compliance requirements with appropriate professionals and authorities.", cta: "Licensing Support", to: "/services#licensing" },
            { icon: Wrench, title: "After-Sales Service", line: "Preventive maintenance, breakdown support, repair, AMC support and spare parts coordination.", cta: "Request Service Support", to: "/services#after-sales" },
          ].map((b) => (
            <StaggerItem key={b.title}>
              <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.07)]" data-testid={`pillar-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                  <b.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-heading text-xl font-semibold text-ink">{b.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{b.line}</p>
                <Link to={b.to} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-700 transition-colors hover:text-accent-600">
                  {b.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* INDUSTRIES */}
    <section className="py-24 md:py-32" data-testid="industries-section">
      <div className="container-x">
        <SectionHead
          eyebrow="Industries We Serve"
          title="Built for every healthcare setting."
        />
        <Stagger className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-9">
          {INDUSTRIES.map((ind) => (
            <StaggerItem key={ind.label}>
              <Link
                to="/industries"
                className="group flex h-full flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-brand-300"
                data-testid={`industry-${ind.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <span className="text-brand-600 transition-colors group-hover:text-accent-600">
                  {typeof ind.icon === "string" ? <Icon name={ind.icon} className="h-7 w-7" /> : <ind.icon className="h-7 w-7" />}
                </span>
                <span className="text-[13px] font-semibold leading-snug text-ink">{ind.label}</span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* WHY CLIENTS CHOOSE US */}
    <section className="bg-brand-50 py-24 md:py-32" data-testid="why-clients">
      <div className="container-x">
        <SectionHead eyebrow="Why Clients Choose Us" title="A partner, not a price list." />
        <Stagger className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w) => (
            <StaggerItem key={w.title}>
              <div className="flex h-full gap-5 rounded-xl border border-brand-100 bg-white p-8" data-testid={`why-card-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white">
                  <w.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.line}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* TESTIMONIALS */}
    <section className="py-24 md:py-32" data-testid="testimonials-section">
      <div className="container-x">
        <SectionHead
          eyebrow="Testimonials"
          title="In their words."
          lede="Client feedback published with the client's consent."
        />
        <Stagger className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t) => (
            <StaggerItem key={t.id}>
              <div className="h-full rounded-xl border border-slate-200 bg-white p-8" data-testid="testimonial-card">
                <Quote className="h-7 w-7 text-accent" />
                <p className="mt-4 text-sm md:text-base leading-relaxed text-slate-700">“{t.testimonial}”</p>
                <div className="mt-6 border-t border-slate-100 pt-4">
                  <p className="font-heading text-base font-semibold text-ink">{t.client}</p>
                  <p className="text-xs text-slate-500">{[t.designation, t.organisation].filter(Boolean).join(", ")}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* RESOURCES */}
    <section className="bg-slate-100 py-24 md:py-32" data-testid="resources-section">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead
            eyebrow="Resource Centre"
            title="Healthcare knowledge for decision-makers."
          />
          <Reveal>
            <Link to="/resources" className="btn-outline" data-testid="resources-view-all">All Resources <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
        <Stagger className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {posts.map((p) => (
            <StaggerItem key={p.id}>
              <Link
                to={`/resources/${p.slug}`}
                className="group block h-full overflow-hidden rounded-xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-[0_20px_40px_rgba(0,0,0,0.07)]"
                data-testid={`resource-card-${p.slug}`}
              >
                <div className="h-48 overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                </div>
                <div className="p-7">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent-600">{p.category}</span>
                  <h3 className="mt-2 font-heading text-lg font-semibold leading-snug text-ink">{p.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{p.excerpt}</p>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <CTABand />
  </main>
);

export default Home;
